import { useRef } from 'react';

import { ExpandMore } from '@mui/icons-material';
import { Box, Chip, SxProps, Theme, Typography } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';

import { useLanguage } from '../../hooks';
import { AssetSummary, AssetTypeWithAssets } from '../../types/asset';
import { Checkbox } from '../uiComponents/ComponentLibrary/Checkbox';
import { getAssetTypeIconColors, getAssetTypeIconComponent } from './utils/assetTypeIcons';

const customStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px',
  },
  assetTypeRow: {
    border: '1px solid #DDE0E5',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  assetTypeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    cursor: 'pointer',
    backgroundColor: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#F8F9FA',
    },
  },
  assetTypeHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    width: '24px',
    height: '24px',
    flexShrink: 0,
  },
  assetTypeName: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#3B3C40',
    whiteSpace: 'nowrap',
  },
  tagsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  countChip: {
    height: '22px',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '4px',
    '& .MuiChip-label': {
      paddingLeft: '8px',
      paddingRight: '8px',
    },
  },
  assetsCountChip: {
    backgroundColor: '#D8EFFE',
    border: '1px solid #8BC6FD',
    color: (theme) => theme.palette.primary.main,
  },
  selectedCountChip: {
    backgroundColor: '#F0F5FF',
    border: '1px solid #ADC6FF',
    color: '#1D39C4',
  },
  chevron: {
    transition: 'transform 0.2s',
    color: '#6E7178',
    fontSize: '20px',
  },
  chevronExpanded: {
    transform: 'rotate(180deg)',
  },
  assetList: {
    borderTop: '1px solid #DDE0E5',
    backgroundColor: '#FAFAFA',
    maxHeight: '400px',
    overflowY: 'auto',
  },
  assetItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px 10px 48px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#F0F5FF',
    },
  },
  assetName: {
    fontSize: '14px',
    color: '#3B3C40',
  },
  assetCode: {
    fontSize: '12px',
    color: '#B8BABF',
    marginLeft: '4px',
  },
} satisfies Record<string, SxProps<Theme>>;

const ASSET_ITEM_HEIGHT = 54;
const MAX_VISIBLE_ITEMS = 10;

interface VirtualizedAssetListProps {
  assetTypeId: number;
  assets: AssetSummary[];
  isAssetSelected: (assetTypeId: number, assetId: number) => boolean;
  toggleAsset: (assetTypeId: number, assetId: number, allAssets: AssetSummary[]) => void;
}

function VirtualizedAssetList({
  assetTypeId,
  assets,
  isAssetSelected,
  toggleAsset,
}: VirtualizedAssetListProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const rowVirtualizer = useVirtualizer({
    count: assets.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ASSET_ITEM_HEIGHT,
    overscan: 5,
  });

  const listHeight = Math.min(assets.length, MAX_VISIBLE_ITEMS) * ASSET_ITEM_HEIGHT;

  return (
    <Box
      ref={scrollRef}
      sx={{ ...customStyles.assetList, height: listHeight }}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualItem) => {
          const asset = assets[virtualItem.index];
          return (
            <Box
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={rowVirtualizer.measureElement}
              sx={customStyles.assetItem}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
              }}
              onClick={() => toggleAsset(assetTypeId, asset.id, assets)}
            >
              <Checkbox checked={isAssetSelected(assetTypeId, asset.id)} />
              <Typography sx={customStyles.assetName}>{asset.name}</Typography>
              <Typography sx={customStyles.assetCode}>{asset.code}</Typography>
            </Box>
          );
        })}
      </div>
    </Box>
  );
}

interface AssetTypesMultipleSelectorProps {
  assetTypes: AssetTypeWithAssets[];
  expandedAssetTypeIds: number[];
  toggleAssetType: (assetTypeId: number) => void;
  toggleAssetTypeExpansion: (assetTypeId: number) => void;
  toggleAsset: (assetTypeId: number, assetId: number, allAssets: AssetSummary[]) => void;
  isAssetTypeSelected: (assetTypeId: number) => boolean;
  isAssetSelected: (assetTypeId: number, assetId: number) => boolean;
  getSelectedAssetsCount: (assetTypeId: number, totalAssets: number) => number;
}

export function AssetTypesMultipleSelector({
  assetTypes,
  expandedAssetTypeIds,
  toggleAssetType,
  toggleAssetTypeExpansion,
  toggleAsset,
  isAssetTypeSelected,
  isAssetSelected,
  getSelectedAssetsCount,
}: AssetTypesMultipleSelectorProps) {
  const lang = useLanguage();

  const handleSelectAssetType = (event: React.MouseEvent<HTMLDivElement>, assetTypeId: number) => {
    event.stopPropagation();
    toggleAssetType(assetTypeId);
  };

  return (
    <Box sx={customStyles.container}>
      {assetTypes.map((assetType) => {
        const isExpanded = expandedAssetTypeIds.includes(assetType.id);
        const isSelected = isAssetTypeSelected(assetType.id);
        const selectedCount = getSelectedAssetsCount(assetType.id, assetType.assets_count);
        const IconComponent = getAssetTypeIconComponent(assetType.icon ?? undefined);
        const { fill, stroke } = getAssetTypeIconColors(assetType.icon_color ?? undefined);

        return (
          <Box
            key={assetType.id}
            sx={customStyles.assetTypeRow}
          >
            <Box
              sx={customStyles.assetTypeHeader}
              onClick={() => toggleAssetTypeExpansion(assetType.id)}
            >
              <Box sx={customStyles.assetTypeHeaderLeft}>
                <Box onClick={(event) => handleSelectAssetType(event, assetType.id)}>
                  <Checkbox checked={isSelected} />
                </Box>
                <Box
                  sx={{
                    ...customStyles.iconWrapper,
                    backgroundColor: fill,
                  }}
                >
                  <IconComponent
                    sx={{ fontSize: '16px' }}
                    stroke={stroke}
                  />
                </Box>
                <Typography sx={customStyles.assetTypeName}>{assetType.name}</Typography>
                <Box sx={customStyles.tagsRow}>
                  <Chip
                    label={`${assetType.assets_count} ${lang.mainMenu.assets}`}
                    size='small'
                    sx={{ ...customStyles.countChip, ...customStyles.assetsCountChip }}
                  />
                  {selectedCount > 0 && (
                    <Chip
                      label={lang.assets.selectedCount.replace('{count}', String(selectedCount))}
                      size='small'
                      sx={{ ...customStyles.countChip, ...customStyles.selectedCountChip }}
                    />
                  )}
                </Box>
              </Box>
              <ExpandMore
                sx={{
                  ...customStyles.chevron,
                  ...(isExpanded ? customStyles.chevronExpanded : {}),
                }}
              />
            </Box>

            {isExpanded && (
              <VirtualizedAssetList
                assetTypeId={assetType.id}
                assets={assetType.assets}
                isAssetSelected={isAssetSelected}
                toggleAsset={toggleAsset}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}
